import { useTranslation } from "react-i18next";
import { useOrder } from "@/store/orderStore";
import { useAuth } from "@/auth/AuthProvider";
import { useAuthSheet } from "@/auth/AuthSheetProvider";
import Avatar from "@/components/Avatar";
import { useTenant } from "@/tenant/TenantProvider";
import TenantHeader from "@/components/TenantHeader";
import { EVENTS, track } from "@/lib/analytics";
import { ChevronRight, Zap, Utensils, User, GlassWater, BookOpen, QrCode } from "lucide-react";

const ModeSelect = ({ onBuild, onAuto, onDrinks, onMenu, onOpenProfile, onChangeTable }) => {
  const { state } = useOrder();
  const { restaurant } = useTenant();
  const { user } = useAuth();
  const { openSignIn } = useAuthSheet();
  const { t } = useTranslation();
  const g = state.guest;
  const displayName = user?.name?.split(" ")[0] || g.name;
  // A restaurant that has the AI switched off must not be offered it: the guest
  // used to answer five questions before the server told them so, which reads as
  // a broken restaurant rather than a feature this one does not have.
  const aiEnabled = restaurant?.ai?.enabled !== false;

  // Which door the guest took. `ai_offered` travels with it because the answer
  // "nobody chose the AI" means something different on a menu that never showed
  // it — a restaurant with the feature switched off would otherwise read as a
  // restaurant whose guests refused it.
  const choose = (mode, next) => () => {
    track(EVENTS.MODE_SELECTED, { mode, ai_offered: aiEnabled });
    next();
  };

  return (
    <div className="px-5 pt-8 pb-12 flex flex-col min-h-screen">
      <TenantHeader
        className="mb-6"
        right={
          user ? (
            <button
              data-testid="header-profile-btn"
              onClick={onOpenProfile}
              aria-label={t("modeSelect.openProfile")}
              className="active:scale-95 transition-transform"
            >
              <Avatar name={user.name} email={user.email} picture={user.picture} size={40} testId="header-avatar" />
            </button>
          ) : (
            <button
              data-testid="header-signin-btn"
              onClick={openSignIn}
              className="h-10 px-3 rounded-full bg-white border border-neutral-200 text-neutral-800 text-xs font-bold flex items-center gap-1.5 active:scale-95 transition-transform"
            >
              <User size={14} /> {t("modeSelect.signIn")}
            </button>
          )
        }
      />

      {/* Where this order is going, worn as a badge rather than a sentence — and
          the way back to the screen that decides it. This step is a URL, so it
          gets bookmarked and reopened days later; every other screen has a back
          arrow to here, and here had nothing at all, which left a guest looking
          at a table number from a previous visit with no control anywhere on the
          screen to correct it. It is also the only place the table is mentioned,
          so a second "change table" button would be two controls for one fact.
          With no table yet, it says what tapping it is for rather than drawing a
          dash where a number belongs. */}
      <div className="mb-8 flex items-center min-w-0">
        <button
          data-testid="mode-table-badge"
          onClick={onChangeTable}
          aria-label={t("modeSelect.changeTable")}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-neutral-900 text-white text-[11px] font-black uppercase tracking-wider shrink-0 active:scale-[0.97] transition-transform"
        >
          <QrCode size={12} className="shrink-0" />
          {g.fulfillment === "takeaway"
            ? t("guestEntry.takeaway")
            : g.table
              ? t("common.table", { number: g.table })
              : t("modeSelect.chooseTable")}
        </button>
      </div>

      {/* The greeting leads into the question on its own line — beside the
          table badge it read as the badge's caption, not as a hello. */}
      <p className="text-base font-semibold text-neutral-600 mb-1 truncate">
        {displayName ? t("modeSelect.greeting", { name: displayName }) : t("modeSelect.ready")}
      </p>
      {/* The line break lives in the catalog: where a two-line headline splits is
          a translator's call, not the layout's. */}
      <h1 className="text-[40px] leading-[1.05] font-black tracking-tight text-neutral-900 mb-2 whitespace-pre-line">
        {t("modeSelect.title")}
      </h1>
      <p className="text-base text-neutral-600 mb-8">{t("modeSelect.subtitle")}</p>

      <div className="space-y-4 flex-1">
        {/* The house recommendation comes first. Building by hand is the whole
            menu and always will be; the AI is the thing a guest has to be told
            about, and it sat below the fold of a card they had already read. */}
        {aiEnabled && (
          <ModeCard
            testid="mode-auto"
            onClick={choose("auto", onAuto)}
            icon={<Zap size={28} strokeWidth={2.2} />}
            title={t("modeSelect.autoTitle")}
            subtitle={t("modeSelect.autoSubtitle", {
              persona: restaurant?.ai?.persona_name || "Fuelly",
            })}
            accent="orange"
          />
        )}
        <ModeCard
          testid="mode-build"
          onClick={choose("build", onBuild)}
          icon={<Utensils size={28} strokeWidth={2.2} />}
          title={t("modeSelect.buildTitle")}
          subtitle={t("modeSelect.buildSubtitle")}
          accent="dark"
        />
        <ModeCard
          testid="mode-menu"
          onClick={choose("menu", onMenu)}
          icon={<BookOpen size={28} strokeWidth={2.2} />}
          title={t("modeSelect.menuTitle")}
          subtitle={t("modeSelect.menuSubtitle")}
          accent="paper"
        />
        <ModeCard
          testid="mode-drinks"
          onClick={choose("drinks", onDrinks)}
          icon={<GlassWater size={28} strokeWidth={2.2} />}
          title={t("modeSelect.drinksTitle")}
          subtitle={t("modeSelect.drinksSubtitle")}
          accent="water"
        />
      </div>

    </div>
  );
};

const ModeCard = ({ testid, onClick, icon, title, subtitle, accent }) => {
  const isDark = accent === "dark";
  const isWater = accent === "water";
  const isPaper = accent === "paper";
  const isCompact = isWater || isPaper;
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`w-full text-left rounded-3xl p-6 relative overflow-hidden active:scale-[0.98] transition-transform ${
        isDark
          ? "bg-neutral-900 text-white"
          : isWater
          ? "bg-gradient-to-br from-[#DFF3FB] via-[#CFEAF7] to-[#B7DCF0] text-[#0B4460] border border-[#9FCDE4]/60"
          : isPaper
          ? "bg-white text-neutral-900 border border-neutral-200"
          : "bg-[#E34A27] text-white"
      }`}
      style={{ minHeight: isCompact ? 140 : 180 }}
    >
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: isWater
            ? "radial-gradient(#0B4460 1px, transparent 1px)"
            : isPaper
            ? "radial-gradient(#171717 1px, transparent 1px)"
            : "radial-gradient(rgba(255,255,255,1) 1px, transparent 1px)",
          backgroundSize: "5px 5px",
        }}
      />
      <div className="relative flex flex-col h-full justify-between gap-4">
        <div
          className={`w-12 h-12 rounded-full flex items-center justify-center ${
            isWater ? "bg-white/60 text-[#0B4460]" : isPaper ? "bg-neutral-100 text-neutral-900" : "bg-white/10"
          }`}
        >
          {icon}
        </div>
        <div>
          <div className={`font-black tracking-tight leading-none ${isCompact ? "text-xl" : "text-2xl"}`}>
            {title}
          </div>
          <div
            className={`text-sm mt-1.5 ${
              isWater ? "text-[#0B4460]/70" : isPaper ? "text-neutral-500" : "opacity-80"
            }`}
          >
            {subtitle}
          </div>
        </div>
        <ChevronRight
          size={22}
          className={`absolute top-6 right-6 ${
            isWater ? "text-[#0B4460]/60" : isPaper ? "text-neutral-400" : "opacity-80"
          }`}
        />
      </div>
    </button>
  );
};

export default ModeSelect;
